import React, { Fragment, useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';
import { Button, ButtonToolbar, Card, CardBody, Col, Row, Badge } from 'reactstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector, reset } from 'redux-form';
import {
  BANNERS_SUCCESS,
  getBannersList as getBannersListAction,
  bannerClearState as bannerClearStateAction,
  bannerClearFlashMessage as bannerClearFlashMessageAction,
} from '../../../../redux/actions/bannersAction';
import renderField from '../../../../shared/components/form/TextField';
import renderSelectField from '../../../../shared/components/form/Select';
import DataTable from '../../../../shared/components/data-table';
import { COMPLETED, WAIT } from '../../../../shared/components/data-table/submitStatuses';
import { DATA, ACTION } from '../../../../shared/components/data-table/columnTypes';
import { LINK } from '../../../../shared/components/data-table/buttonTypes';
import { FullWideNotification } from '../../../../shared/components/Notification';
import store from '../../../App/store';

const BannerTable = ({
  getBannersList,
  bannerClearState,
  bannerClearFlashMessage,
  showNotification,
  bannersList,
  pagination,
  loadStatus,
  flashMessage,
  resetForm,
}) => {
  const [data, setData] = useState({
    columns: [],
    buttons: [],
    types: [],
    isFind: false,
    status: WAIT,
  });

  const clearFilters = () => {
    resetForm(BannerTable.FORM_NAME);
    setData({ ...data, isFind: true });
  };

  const getConditions = () => {
    const selector = formValueSelector(BannerTable.FORM_NAME);
    return selector(store.getState(), 'name', 'type');
  };

  const handleFind = () => {
    setData({ ...data, isFind: true });
  };

  const show = (color, message) =>
    showNotification({
      notification: <FullWideNotification color={color} message={message} />,
      position: 'full',
    });

  useEffect(() => {
    if (loadStatus === BANNERS_SUCCESS) {
      setData({ ...data, isFind: false });
    }
  }, [loadStatus]);

  useEffect(() => {
    if (flashMessage && flashMessage.length) {
      setTimeout(() => {
        show('success', flashMessage);
        bannerClearFlashMessage();
      }, 1000);
    }
  }, [flashMessage]);

  useEffect(() => {
    const types = [
      {
        value: '1',
        label: 'страница Мои курсы',
      },
      {
        value: '2',
        label: 'страница Витрина',
      },
    ];

    const columns = [
      {
        label: '#',
        dbColumn: 'createdAt',
        listColumn: 'number',
        sortable: true,
        width: 5,
        type: DATA,
      },
      {
        label: 'Название баннера',
        dbColumn: 'name',
        listColumn: 'name',
        sortable: false,
        width: 25,
        type: DATA,
      },
      {
        label: 'Отображать на',
        dbColumn: 'type',
        listColumn: 'type',
        sortable: false,
        width: 20,
        type: DATA,
      },
      {
        label: 'Показывать',
        dbColumn: 'isVisible',
        listColumn: 'isVisible',
        sortable: true,
        width: 15,
        handle: (isVisible) =>
          isVisible ? <Badge color="success">Отображается</Badge> : <Badge color="danger">Скрытый</Badge>,
        type: DATA,
      },
      {
        label: 'Начать',
        dbColumn: 'start',
        listColumn: 'start',
        sortable: false,
        width: 15,
        type: DATA,
      },
      {
        label: 'Окончить',
        dbColumn: 'end',
        listColumn: 'end',
        sortable: false,
        width: 15,
        type: DATA,
      },
      {
        label: 'Действия',
        dbColumn: '_id',
        listColumn: 'id',
        sortable: false,
        width: 5,
        type: ACTION,
      },
    ];

    const buttons = [
      {
        name: 'edit',
        color: 'primary',
        icon: 'edit',
        type: LINK,
        link: '/banners/',
        text: 'Редактировать',
      },
    ];

    setData({
      ...data,
      columns,
      buttons,
      types,
      status: COMPLETED,
    });

    return () => {
      bannerClearState();
    };
  }, []);

  return (
    <Fragment>
      {data.status === COMPLETED && (
        <Fragment>
          <Row>
            <Col md={12} lg={12}>
              <Card>
                <CardBody>
                  <div className="card__title">
                    <h5 className="bold-text">Поиск по баннерам</h5>
                  </div>
                  <form className="form gap-20">
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Название баннера</span>
                        <div className="form__form-group-field">
                          <Field name="name" component={renderField} type="text" placeholder="Название баннера" />
                        </div>
                      </div>
                    </Col>
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Отображать на</span>
                        <div className="form__form-group-field">
                          <Field
                            name="type"
                            component={renderSelectField}
                            placeholder="Отображать на"
                            options={data.types}
                          />
                        </div>
                      </div>
                    </Col>
                  </form>
                  <ButtonToolbar className="form__button-toolbar wizard__toolbar">
                    <Button color="primary" type="button" onClick={handleFind}>
                      Найти
                    </Button>
                    <Button color="secondary" type="button" onClick={clearFilters}>
                      Очистить
                    </Button>
                  </ButtonToolbar>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={12} lg={12}>
              <DataTable
                columns={data.columns}
                buttons={data.buttons}
                getConditions={getConditions}
                getList={getBannersList}
                list={bannersList}
                pagination={pagination}
                isFind={data.isFind}
                loadListStatus={loadStatus}
                declensions={['баннер', 'баннера', 'баннеров']}
              />
            </Col>
          </Row>
        </Fragment>
      )}
    </Fragment>
  );
};

BannerTable.FORM_NAME = 'banners_table';

BannerTable.propTypes = {
  getBannersList: PropTypes.func.isRequired,
  bannerClearState: PropTypes.func.isRequired,
  bannerClearFlashMessage: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  bannersList: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  // eslint-disable-next-line react/require-default-props
  pagination: PropTypes.objectOf(PropTypes.any),
  loadStatus: PropTypes.string.isRequired,
  flashMessage: PropTypes.string.isRequired,
  resetForm: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  bannersList: state.banners.list,
  pagination: state.banners.pagination,
  loadStatus: state.banners.loadListStatus,
  flashMessage: state.banners.flash,
});

const mapDispatchToProps = (dispatch) => ({
  getBannersList: (params) => dispatch(getBannersListAction(params)),
  bannerClearState: () => dispatch(bannerClearStateAction()),
  bannerClearFlashMessage: () => dispatch(bannerClearFlashMessageAction()),
  resetForm: (formName) => dispatch(reset(formName)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps)(withTranslation('common')(withRouter(BannerTable)));

export default reduxForm({
  form: BannerTable.FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(withConnect);
